import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Save } from 'lucide-react';
import { stockConfigService } from '@/services/stockConfigService';
import type { LocationApiResponse, TransferStockItem } from '@/services/stockConfigService';
import { toast } from 'sonner';
import type { Location, SafetyStock, DayOfWeek } from './types/stock-config.types';

interface Category {
  id: number;
  name: string;
}

const DAYS_ORDER: DayOfWeek[] = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const DAY_LABELS: Record<DayOfWeek, string> = {
  lunes: 'Lun', martes: 'Mar', miercoles: 'Mié',
  jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom',
};

const normalizeDay = (day: string) =>
  day.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const StockConfigPage: React.FC = () => {
  const [safetyStocks, setSafetyStocks] = useState<SafetyStock[]>([]);
  const [transferStocks, setTransferStocks] = useState<TransferStockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingTransfers, setSavingTransfers] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // Edits on existing records (keyed by record id)
  const [editedItems, setEditedItems] = useState<Record<string, number>>({});
  const [editedTransferItems, setEditedTransferItems] = useState<Record<string, number>>({});

  // New values for cells without a record (keyed by "ingredientId-locationId" or "ingredientId-day-shift")
  const [newPurchaseItems, setNewPurchaseItems] = useState<Record<string, number>>({});
  const [newTransferItems, setNewTransferItems] = useState<Record<string, number>>({});

  const [activeTab, setActiveTab] = useState<'transfers' | 'purchase-orders'>('purchase-orders');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [selectedTransferLocation, setSelectedTransferLocation] = useState<string>('');

  // ── API loading ──────────────────────────────────────────────

  const loadLocations = async () => {
    try {
      const response: LocationApiResponse = await stockConfigService.getLocations();
      const mapped: Location[] = response.data.map(loc => ({ id: loc.id, name: loc.nombre }));
      setLocations(mapped);
      if (mapped.length > 0) {
        setSelectedTransferLocation(prev => prev || mapped[0].id);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await stockConfigService.getCategories();
      if (response.success) setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStockConfig = async () => {
    try {
      setLoading(true);
      const response = await stockConfigService.getStockConfig({}, 1, 5000);
      if (response.safetyStocks) setSafetyStocks(response.safetyStocks.items);
    } catch (error) {
      console.error('Error loading stock config:', error);
      toast.error('Error al cargar configuración de stock');
    } finally {
      setLoading(false);
    }
  };

  const loadTransferConfig = async () => {
    try {
      // Load all pages - there can be 20k+ records
      let allItems: TransferStockItem[] = [];
      let page = 1;
      const pageSize = 5000;
      let hasMore = true;

      while (hasMore) {
        const response = await stockConfigService.getTransferConfig({}, page, pageSize);
        if (response.transferStocks) {
          allItems = [...allItems, ...response.transferStocks.items];
          hasMore = allItems.length < response.transferStocks.totalCount;
          page++;
        } else {
          hasMore = false;
        }
      }
      setTransferStocks(allItems);
    } catch (error) {
      console.error('Error loading transfer config:', error);
      toast.error('Error al cargar configuración de transferencias');
    }
  };

  useEffect(() => {
    loadLocations();
    loadCategories();
    loadStockConfig();
    loadTransferConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Ingredient list helpers ──────────────────────────────────

  interface IngredientRow {
    id: string;
    name: string;
    category: string;
    unit: string;
  }

  const applyIngredientFilters = (items: Array<{ ingredient: { name: string; category?: string }; ingredientId: string | number; unit: string }>) => {
    let filtered = items;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => item.ingredient.name.toLowerCase().includes(term));
    }
    if (categoryFilter) {
      const cat = categories.find(c => String(c.id) === categoryFilter);
      if (cat) {
        filtered = filtered.filter(item =>
          item.ingredient.category?.toLowerCase().trim() === cat.name.toLowerCase().trim()
        );
      }
    }
    return filtered;
  };

  const buildIngredientList = (items: Array<{ ingredient: { name: string; category?: string }; ingredientId: string | number; unit: string }>): IngredientRow[] => {
    const map = new Map<string, IngredientRow>();
    items.forEach(item => {
      const id = String(item.ingredientId);
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: item.ingredient.name,
          category: item.ingredient.category || '',
          unit: item.unit,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // ── Purchase Orders data ─────────────────────────────────────

  const purchaseData = useMemo(() => {
    const filtered = applyIngredientFilters(safetyStocks);
    const ingredients = buildIngredientList(filtered);

    // Lookup: "ingredientId-locationId" -> SafetyStock
    const lookup = new Map<string, SafetyStock>();
    filtered.forEach(item => {
      lookup.set(`${String(item.ingredientId)}-${item.locationId}`, item);
    });

    return { ingredients, lookup };
  }, [safetyStocks, searchTerm, categoryFilter, categories]);

  // ── Transfer data ────────────────────────────────────────────

  const transferData = useMemo(() => {
    // Ingredients from ALL locations (not filtered by selected location)
    const filtered = applyIngredientFilters(transferStocks);
    const ingredients = buildIngredientList(filtered);

    // Values lookup: only for selected location
    const lookup = new Map<string, TransferStockItem>();
    filtered
      .filter(item => String(item.locationId) === String(selectedTransferLocation))
      .forEach(item => {
        lookup.set(
          `${String(item.ingredientId)}-${normalizeDay(item.dayOfWeek)}-${item.shift}`,
          item
        );
      });

    return { ingredients, lookup };
  }, [transferStocks, searchTerm, categoryFilter, categories, selectedTransferLocation]);

  // ── Handlers ─────────────────────────────────────────────────

  const handlePurchaseChange = (stock: SafetyStock | null, ingredientId: string, locationId: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    if (stock && stock.id) {
      setEditedItems(prev => ({ ...prev, [stock.id]: numericValue }));
    } else {
      const key = `${ingredientId}-${locationId}`;
      setNewPurchaseItems(prev => ({ ...prev, [key]: numericValue }));
    }
  };

  const handleTransferChange = (stock: TransferStockItem | null, ingredientId: string, day: DayOfWeek, shift: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    if (stock && stock.id) {
      setEditedTransferItems(prev => ({ ...prev, [stock.id]: numericValue }));
    } else {
      const key = `${ingredientId}-${selectedTransferLocation}-${day}-${shift}`;
      setNewTransferItems(prev => ({ ...prev, [key]: numericValue }));
    }
  };

  const handleSavePurchaseOrders = async () => {
    const hasEdits = Object.keys(editedItems).length > 0;
    const newEntries = Object.entries(newPurchaseItems).filter(([, v]) => v > 0);
    const hasNew = newEntries.length > 0;

    if (!hasEdits && !hasNew) {
      toast.info('No hay cambios para guardar');
      return;
    }

    setSaving(true);
    try {
      // Update existing records
      if (hasEdits) {
        const updates = Object.entries(editedItems).map(([id, minStock]) => ({ id, minStock }));
        const response = await stockConfigService.bulkUpdateSafetyStock(updates);
        if (response.errors?.length) toast.warning(`${response.errors.length} errores al actualizar`);
      }

      // Create new records
      let created = 0;
      for (const [key, minStock] of newEntries) {
        const [ingredientId, locationId] = key.split('-');
        try {
          await stockConfigService.createSafetyStock({ ingredientId, locationId, minStock });
          created++;
        } catch (error) {
          console.error(`Error creating safety stock for ${key}:`, error);
        }
      }

      const totalChanges = Object.keys(editedItems).length + created;
      toast.success(`${totalChanges} configuraciones guardadas`);
      setEditedItems({});
      setNewPurchaseItems({});
      await loadStockConfig();
    } catch (error) {
      console.error('Error saving stock config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTransfers = async () => {
    const hasEdits = Object.keys(editedTransferItems).length > 0;
    const newEntries = Object.entries(newTransferItems).filter(([, v]) => v > 0);
    const hasNew = newEntries.length > 0;

    if (!hasEdits && !hasNew) {
      toast.info('No hay cambios para guardar');
      return;
    }

    setSavingTransfers(true);
    try {
      // Update existing records
      if (hasEdits) {
        const updates = Object.entries(editedTransferItems).map(([id, minStock]) => ({ id, minStock }));
        const response = await stockConfigService.bulkUpdateTransferStock(updates);
        if (response.errors?.length) toast.warning(`${response.errors.length} errores al actualizar`);
      }

      // Create new records
      let created = 0;
      for (const [key, minStock] of newEntries) {
        const [ingredientId, locationId, dayOfWeek, shift] = key.split('-');
        try {
          await stockConfigService.createTransferStock({ ingredientId, locationId, dayOfWeek, shift, minStock });
          created++;
        } catch (error) {
          console.error(`Error creating transfer stock for ${key}:`, error);
        }
      }

      const totalChanges = Object.keys(editedTransferItems).length + created;
      toast.success(`${totalChanges} configuraciones guardadas`);
      setEditedTransferItems({});
      setNewTransferItems({});
      await loadTransferConfig();
    } catch (error) {
      console.error('Error saving transfer config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingTransfers(false);
    }
  };

  // ── Change counts ────────────────────────────────────────────

  const purchaseChangeCount = Object.keys(editedItems).length +
    Object.values(newPurchaseItems).filter(v => v > 0).length;

  const transferChangeCount = Object.keys(editedTransferItems).length +
    Object.values(newTransferItems).filter(v => v > 0).length;

  // ── Render: Purchase Orders Matrix ───────────────────────────

  const renderPurchaseOrdersMatrix = () => {
    const { ingredients, lookup } = purchaseData;

    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Stock Mínimo - Órdenes de Compra</h3>
            <p className="text-gray-500 text-sm mt-1">
              {ingredients.length} ingredientes &middot; {locations.length} locales
            </p>
          </div>
          <div className="flex items-center gap-3">
            {purchaseChangeCount > 0 && (
              <span className="text-sm text-amber-600 font-medium">
                {purchaseChangeCount} cambio{purchaseChangeCount !== 1 ? 's' : ''} sin guardar
              </span>
            )}
            <Button
              disabled={purchaseChangeCount === 0 || saving}
              onClick={handleSavePurchaseOrders}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Guardando...' : 'Grabar'}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                  Ingrediente
                </th>
                <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                  Categoría
                </th>
                <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]">
                  Und
                </th>
                {locations.map(loc => (
                  <th
                    key={loc.id}
                    className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]"
                  >
                    {loc.name.replace('Macartur ', '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ingredients.length === 0 ? (
                <tr>
                  <td colSpan={3 + locations.length} className="py-12 text-center text-gray-400">
                    No se encontraron ingredientes
                  </td>
                </tr>
              ) : (
                ingredients.map(ing => (
                  <tr key={ing.id} className="hover:bg-gray-50/50">
                    <td className="sticky left-0 z-10 bg-white py-2.5 px-4 font-medium text-gray-900 text-sm">
                      {ing.name}
                    </td>
                    <td className="py-2.5 px-3 text-gray-500 text-sm capitalize">
                      {ing.category}
                    </td>
                    <td className="py-2.5 px-3 text-center text-gray-400 text-xs">
                      {ing.unit}
                    </td>
                    {locations.map(loc => {
                      const stock = lookup.get(`${ing.id}-${loc.id}`);
                      const newKey = `${ing.id}-${loc.id}`;

                      if (stock && stock.id) {
                        // Existing record (has config in purchase_order_safety_stocks)
                        const isEdited = editedItems[stock.id] !== undefined;
                        const value = isEdited ? editedItems[stock.id] : stock.minStock;
                        return (
                          <td key={loc.id} className="py-2.5 px-3 text-center">
                            <Input
                              type="number"
                              value={value}
                              onChange={e => handlePurchaseChange(stock, ing.id, loc.id, e.target.value)}
                              className={`w-20 mx-auto text-center text-sm h-8 ${
                                isEdited ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200' : ''
                              }`}
                              min="0"
                              step="1"
                            />
                          </td>
                        );
                      }

                      // No config record exists - show editable input for new entry
                      const newValue = newPurchaseItems[newKey];
                      const hasNewValue = newValue !== undefined && newValue > 0;
                      return (
                        <td key={loc.id} className="py-2.5 px-3 text-center">
                          <Input
                            type="number"
                            value={newValue !== undefined ? newValue : ''}
                            placeholder="—"
                            onChange={e => handlePurchaseChange(null, ing.id, loc.id, e.target.value)}
                            className={`w-20 mx-auto text-center text-sm h-8 ${
                              hasNewValue
                                ? 'bg-green-50 border-green-300 ring-1 ring-green-200'
                                : 'border-dashed border-gray-200 text-gray-400'
                            }`}
                            min="0"
                            step="1"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render: Transfers Matrix ─────────────────────────────────

  const renderTransfersMatrix = () => {
    const { ingredients, lookup } = transferData;

    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Stock Mínimo - Transferencias</h3>
            <p className="text-gray-500 text-sm mt-1">
              {ingredients.length} ingredientes &middot; por día y turno
            </p>
          </div>
          <div className="flex items-center gap-3">
            {transferChangeCount > 0 && (
              <span className="text-sm text-amber-600 font-medium">
                {transferChangeCount} cambio{transferChangeCount !== 1 ? 's' : ''} sin guardar
              </span>
            )}
            <Button
              disabled={transferChangeCount === 0 || savingTransfers}
              onClick={handleSaveTransfers}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingTransfers ? 'Guardando...' : 'Grabar'}
            </Button>
          </div>
        </div>

        {/* Location tabs */}
        <div className="px-6 pt-4 flex gap-2 border-b overflow-x-auto">
          {locations.map(loc => (
            <button
              key={loc.id}
              onClick={() => setSelectedTransferLocation(loc.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors whitespace-nowrap ${
                selectedTransferLocation === loc.id
                  ? 'bg-white text-orange-600 border-gray-200 -mb-px'
                  : 'bg-gray-50 text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {loc.name.replace('Macartur ', '')}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {/* Day header row */}
              <tr className="bg-gray-50 border-b">
                <th className="sticky left-0 z-10 bg-gray-50" rowSpan={2}></th>
                <th className="bg-gray-50" rowSpan={2}></th>
                <th className="bg-gray-50" rowSpan={2}></th>
                {DAYS_ORDER.map(day => (
                  <th
                    key={day}
                    colSpan={2}
                    className="py-2 px-1 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200"
                  >
                    {DAY_LABELS[day]}
                  </th>
                ))}
              </tr>
              {/* Shift sub-header row */}
              <tr className="bg-gray-50 border-b">
                {DAYS_ORDER.map(day => (
                  <React.Fragment key={day}>
                    <th className="py-1.5 px-1 text-center border-l border-gray-200 min-w-[60px]">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                        M
                      </span>
                    </th>
                    <th className="py-1.5 px-1 text-center min-w-[60px]">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
                        T
                      </span>
                    </th>
                  </React.Fragment>
                ))}
              </tr>
              {/* Column labels row */}
              <tr className="bg-gray-50/50 border-b">
                <th className="sticky left-0 z-10 bg-gray-50 py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">
                  Ingrediente
                </th>
                <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">
                  Categoría
                </th>
                <th className="py-2 px-3 text-center text-xs font-medium text-gray-500 uppercase min-w-[50px]">
                  Und
                </th>
                {DAYS_ORDER.map(day => (
                  <React.Fragment key={day}>
                    <td className="border-l border-gray-200"></td>
                    <td></td>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ingredients.length === 0 ? (
                <tr>
                  <td colSpan={3 + DAYS_ORDER.length * 2} className="py-12 text-center text-gray-400">
                    {selectedTransferLocation
                      ? 'No se encontraron ingredientes con configuración de transferencia'
                      : 'Selecciona un local para ver la configuración'}
                  </td>
                </tr>
              ) : (
                ingredients.map(ing => (
                  <tr key={ing.id} className="hover:bg-gray-50/50">
                    <td className="sticky left-0 z-10 bg-white py-2 px-4 font-medium text-gray-900 text-sm">
                      {ing.name}
                    </td>
                    <td className="py-2 px-3 text-gray-500 text-sm capitalize">
                      {ing.category}
                    </td>
                    <td className="py-2 px-3 text-center text-gray-400 text-xs">
                      {ing.unit}
                    </td>
                    {DAYS_ORDER.map(day =>
                      (['mañana', 'tarde'] as const).map(shift => {
                        const lookupKey = `${ing.id}-${day}-${shift}`;
                        const stock = lookup.get(lookupKey);
                        const isFirst = shift === 'mañana';
                        const borderClass = isFirst ? 'border-l border-gray-200' : '';

                        if (stock && stock.id) {
                          // Existing record (has config in transfer_safety_stocks)
                          const isEdited = editedTransferItems[stock.id] !== undefined;
                          const value = isEdited ? editedTransferItems[stock.id] : stock.minStock;
                          return (
                            <td key={lookupKey} className={`py-2 px-1 text-center ${borderClass}`}>
                              <Input
                                type="number"
                                value={value}
                                onChange={e => handleTransferChange(stock, ing.id, day, shift, e.target.value)}
                                className={`w-16 mx-auto text-center text-sm h-7 ${
                                  isEdited ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200' : ''
                                }`}
                                min="0"
                                step="1"
                              />
                            </td>
                          );
                        }

                        // No record exists
                        const newKey = `${ing.id}-${selectedTransferLocation}-${day}-${shift}`;
                        const newValue = newTransferItems[newKey];
                        const hasNewValue = newValue !== undefined && newValue > 0;
                        return (
                          <td key={lookupKey} className={`py-2 px-1 text-center ${borderClass}`}>
                            <Input
                              type="number"
                              value={newValue !== undefined ? newValue : ''}
                              placeholder="—"
                              onChange={e => handleTransferChange(null, ing.id, day, shift, e.target.value)}
                              className={`w-16 mx-auto text-center text-sm h-7 ${
                                hasNewValue
                                  ? 'bg-green-50 border-green-300 ring-1 ring-green-200'
                                  : 'border-dashed border-gray-200 text-gray-400'
                              }`}
                              min="0"
                              step="1"
                            />
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Configuración de Stock</h1>
        <p className="text-gray-600 mt-1 text-sm md:text-base">
          Gestión de parámetros de stock mínimo para transferencias y órdenes de compra
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1">
        <button
          onClick={() => setActiveTab('purchase-orders')}
          className={`px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'purchase-orders'
              ? 'bg-orange-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Órdenes de Compra
        </button>
        <button
          onClick={() => setActiveTab('transfers')}
          className={`px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'transfers'
              ? 'bg-orange-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Transferencias
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Buscar Ingrediente
              </label>
              <Input
                type="text"
                placeholder="Nombre del ingrediente..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Categoría
              </label>
              <Select
                value={categoryFilter || 'all'}
                onValueChange={value => setCategoryFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Cargando configuración...</div>
        </div>
      ) : (
        <>
          {activeTab === 'purchase-orders' && renderPurchaseOrdersMatrix()}
          {activeTab === 'transfers' && renderTransfersMatrix()}
        </>
      )}
    </div>
  );
};
